import React, { FC, ReactElement } from 'react';
import { Theme } from '@mui/material/styles';
import { createStyles, makeStyles } from '@mui/styles';
import { clsx } from 'clsx';
import Space, { Type } from './layout/space';
import Text from './text';
import SvgIcon, { SvgIconProps } from './icons/SvgIcon';
import { IconCommon } from '../resources/images';

interface InfoBoxTexProps {
    title?: string;
    titleIcon?: 'info';
    iconColor?: SvgIconProps['color'];
    description: string;
    dangerouslySetInnerHTML?: boolean;
    className?: string;
    align?: 'left' | 'right';
    flexDirection?: 'row' | 'column';
}

const InfoBoxTex: FC<InfoBoxTexProps> = ({
    title,
    titleIcon = 'info',
    iconColor = 'black',
    description,
    dangerouslySetInnerHTML = false,
    className,
    align = 'left',
    flexDirection = 'row',
}): ReactElement => {
    const classes = useStyles();

    const renderIcon = () => {
        return <SvgIcon icon={IconCommon.InformationOutline} color={iconColor} fill="transparent" fix={18} />;
    };

    return (
        <div className={clsx(classes.rootInfoBoxText, className, classes[align], classes[flexDirection])}>
            {title && (
                <>
                    <div className={classes.titleContainer}>
                        {titleIcon && (
                            <>
                                {renderIcon()}
                                <Space value={1} type={Type.horizontal} />
                            </>
                        )}
                        <Text.H3 text={title} className={classes.infoBoxTexTitle} />
                    </div>
                    <Space value={1} />
                </>
            )}
            {!title && titleIcon && renderIcon()}
            <Text.Description14
                text={dangerouslySetInnerHTML ? null : description}
                dangerouslySetInnerHTML={
                    dangerouslySetInnerHTML
                        ? {
                              __html: description,
                          }
                        : null
                }
                className={classes.infoBoxTexDesc}
            />
        </div>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        rootInfoBoxText: {
            flex: 1,
            display: 'flex',
            background: `linear-gradient(0deg, ${theme.palette.colors.border}, ${theme.palette.colors.border}), linear-gradient(0deg, rgba(69, 112, 234, 0.2), rgba(69, 112, 234, 0.2)), #FFFFFF`,
            borderRadius: 4,
            padding: '20px 16px',
            justifyContent: 'center',
            alignItems: 'center',
            columnGap: theme.spacing(1),
            color: theme.palette.primary.main,
        },
        row: {
            flexDirection: 'row',
        },
        column: {
            flexDirection: 'column',
        },
        left: {
            textAlign: 'start',
            alignItems: 'flex-start',
        },
        right: {
            textAlign: 'end',
            alignItems: 'flex-end',
        },
        titleContainer: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
        },
        infoBoxTexTitle: {
            fontSize: 14,
            fontWeight: 400,
        },
        infoBoxTexDesc: {
            fontSize: 14,
            fontWeight: 400,
            color: theme.palette.primary.main,
        },
    }),
);

export default InfoBoxTex;
